import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import CourtPlanLogo from '../components/CourtPlanLogo';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.');
      return;
    }
    if (password !== confirm) {
      setError('Οι κωδικοί δεν ταιριάζουν.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password }, { withCredentials: true });
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f2e7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <CourtPlanLogo size="md" />
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8" data-testid="set-password-card">
          {!token ? (
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-4" data-testid="set-password-missing-token">
                Λείπει ο σύνδεσμος ενεργοποίησης. Ανοίξτε τον σύνδεσμο από το email που σας στείλαμε.
              </p>
              <Link to="/login" className="text-sm text-red-700 font-medium hover:underline" data-testid="set-password-back-login">
                Επιστροφή στη σύνδεση
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <h1 className="text-lg font-semibold text-slate-900">Το email σας επιβεβαιώθηκε</h1>
              </div>
              <p className="text-sm text-slate-600 mb-6">
                Ορίστε τον κωδικό σας για να ενεργοποιήσετε τον λογαριασμό και να συνδεθείτε.
              </p>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="set-password-error">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Κωδικός</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Τουλάχιστον 6 χαρακτήρες"
                      className="w-full h-11 pl-10 pr-12 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                      required
                      autoComplete="new-password"
                      data-testid="set-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="set-password-toggle-visibility"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Επιβεβαίωση κωδικού</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Ξαναγράψτε τον κωδικό"
                      className="w-full h-11 pl-10 pr-4 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                      required
                      autoComplete="new-password"
                      data-testid="set-password-confirm-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-red-700 hover:bg-red-800 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
                  data-testid="set-password-submit-btn"
                >
                  {loading ? '...' : 'ΕΝΕΡΓΟΠΟΙΗΣΗ ΛΟΓΑΡΙΑΣΜΟΥ'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
